import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'

import { QuickscanWidget } from '../QuickscanWidget'
import type { WidgetConfig } from '../config'
import widgetStyles from '../styles.css?inline'

const TAG_NAME = 'wtta-quickscan'

const CONFIG_ATTRIBUTES: Array<[string, keyof WidgetConfig]> = [
  ['pardot-form-url', 'pardotFormUrl'],
  ['pardot-account-id', 'pardotAccountId'],
  ['pardot-form-id', 'pardotFormId'],
  ['pardot-form-host', 'pardotFormHost'],
  ['report-page-url', 'reportPageUrl'],
]

export class WttaQuickscanElement extends HTMLElement {
  private root: Root | null = null
  private mountPoint: HTMLDivElement | null = null

  static get observedAttributes(): string[] {
    return CONFIG_ATTRIBUTES.map(([attr]) => attr)
  }

  connectedCallback(): void {
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: 'open' })
      const styleEl = document.createElement('style')
      styleEl.textContent = widgetStyles
      shadow.appendChild(styleEl)
      this.mountPoint = document.createElement('div')
      shadow.appendChild(this.mountPoint)
    }
    this.render()
  }

  disconnectedCallback(): void {
    this.root?.unmount()
    this.root = null
  }

  attributeChangedCallback(): void {
    this.render()
  }

  private buildConfigOverrides(): Partial<WidgetConfig> {
    const overrides: Partial<WidgetConfig> = {}
    for (const [attr, key] of CONFIG_ATTRIBUTES) {
      const value = this.getAttribute(attr)
      if (value && value.length > 0) {
        overrides[key] = value
      }
    }
    return overrides
  }

  private render(): void {
    if (!this.mountPoint) return
    const overrides = this.buildConfigOverrides()
    const widgetId = this.id || 'wtta-quickscan'

    if (!this.root) {
      this.root = createRoot(this.mountPoint)
    }

    this.root.render(
      <StrictMode>
        <QuickscanWidget config={overrides} widgetId={widgetId} />
      </StrictMode>,
    )
  }
}

export function registerCustomElement(): void {
  if (typeof window === 'undefined') return
  if (window.customElements.get(TAG_NAME)) return
  window.customElements.define(TAG_NAME, WttaQuickscanElement)
}

export const QUICKSCAN_TAG_NAME = TAG_NAME
